import { Anchor, Table, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconCheck, IconEye, IconTrash, IconX } from "@tabler/icons-react";
import { receiptApi } from "apis/receiptApi";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import CustomText from "components/ui/CustomText/CustomText";
import GroupActions from "components/ui/GroupActions";
import { ActionItem } from "components/ui/GroupActions/GroupActions";
import { Loading } from "components";
import { useAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";
import {
  RECEIPT_STATUS,
  RECEIPT_STATUS_COLOR,
  RECEIPT_STATUS_LABEL,
} from "constants/common/receipt";
import { PaymentJournalType } from "constants/types/debt";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DEBT_RECEIPT_DETAIL_PAGE } from "routes/route.constant";
import { formatBankAccount, formatCurrency, formatDate } from "utils/helpers/format";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import useNotification from "utils/hooks/ui/useNotification";
import { useListBankAccount } from "utils/hooks/request/useBankAccount";
import { FORM_IDS } from "../../config";
import ReviewModal from "./ReviewModal";

interface PaymentJournalsTableProps {
  journals: PaymentJournalType[];
  mutate?: () => void;
  hideActions?: boolean;
}

const ICON_SIZE = 18;

const PaymentJournalsTable: FC<PaymentJournalsTableProps> = ({
  journals,
  mutate,
  hideActions = false,
}) => {
  const navigate = useNavigate();
  const ability = useAbility();
  const { openCrudModal, closeCrudModal } = useAppModal();
  const { bankAccountList } = useListBankAccount();
  const bankAccountMap = useMemo(
    () => Object.fromEntries(bankAccountList.map((b) => [b.id, b])),
    [bankAccountList],
  );
  const { notification } = useNotification();
  const [reversingJournalId, setReversingJournalId] = useState<string | null>(null);
  const activeModalId = useRef<string | null>(null);

  const handleReverseReceipt = useCallback(
    (row: PaymentJournalType) => {
      modals.openConfirmModal({
        title: "Xóa phiếu thu",
        children: (
          <Text size="sm">
            Bạn có chắc chắn muốn xóa phiếu thu{" "}
            <Text component="span" fw={600}>
              {row.code}
            </Text>{" "}
            không? Hành động này không thể hoàn tác.
          </Text>
        ),
        labels: { confirm: "Xóa", cancel: "Hủy" },
        confirmProps: { color: "red" },
        onConfirm: async () => {
          setReversingJournalId(row.id);
          try {
            const res = await receiptApi.reverse(row.id);
            if (res.data?.success) {
              notification.success({ title: "Xóa phiếu thu thành công" });
              mutate?.();
            } else {
              notification.error({
                title: "Xóa phiếu thu thất bại",
                message: res.data?.error?.message,
              });
            }
          } catch (err: any) {
            notification.error({
              title: "Xóa phiếu thu thất bại",
              message: err?.response?.data?.error?.message,
            });
          } finally {
            setReversingJournalId(null);
          }
        },
      });
    },
    [mutate, notification],
  );

  const handleCloseModal = useCallback(() => {
    if (activeModalId.current) {
      closeCrudModal(activeModalId.current);
      activeModalId.current = null;
    }
  }, [closeCrudModal]);

  const handleOpenReviewModal = useCallback(
    (journalId: string, action: "approve" | "reject") => {
      const title =
        action === "approve" ? "Xác nhận phiếu thu" : "Từ chối phiếu thu";
      const submitText = action === "approve" ? "Xác nhận" : "Từ chối";

      const modalId = openCrudModal(
        <CustomText variant="section-title">{title}</CustomText>,
        {
          mode: "create",
          formId: FORM_IDS.REVIEW_RECEIPT,
          children: (
            <ReviewModal
              journalId={journalId}
              action={action}
              formId={FORM_IDS.REVIEW_RECEIPT}
              mutate={mutate}
              onClose={handleCloseModal}
            />
          ),
          submitText,
        },
        500,
      );
      activeModalId.current = modalId;
    },
    [openCrudModal, handleCloseModal, mutate],
  );

  const rowActions: ActionItem<PaymentJournalType>[] = useMemo(
    () => [
      {
        key: "view",
        label: "Xem chi tiết",
        icon: <IconEye size={ICON_SIZE} />,
        color: "blue",
        permission: "payment_journal:read",
        onClick: (row) =>
          navigate(DEBT_RECEIPT_DETAIL_PAGE.replace(":id", row.id)),
      },
      {
        key: "approve",
        label: "Xác nhận",
        icon: <IconCheck size={ICON_SIZE} />,
        color: "green",
        permission: "payment_journal:approve",
        hidden: (row) => row.status !== RECEIPT_STATUS.PENDING,
        onClick: (row) => handleOpenReviewModal(row.id, "approve"),
      },
      {
        key: "reject",
        label: "Từ chối",
        icon: <IconX size={ICON_SIZE} />,
        color: "red",
        permission: "payment_journal:approve",
        hidden: (row) => row.status !== RECEIPT_STATUS.PENDING,
        onClick: (row) => handleOpenReviewModal(row.id, "reject"),
      },
      {
        key: "reverse",
        label: "Xóa phiếu thu",
        icon: <IconTrash size={ICON_SIZE} />,
        color: "red",
        permission: "payment_journal:delete",
        hidden: (row) => row.status !== RECEIPT_STATUS.APPROVED,
        disabled: () => reversingJournalId !== null,
        onClick: (row) => handleReverseReceipt(row),
      },
    ],
    [navigate, handleOpenReviewModal, handleReverseReceipt, reversingJournalId],
  );

  const hasActionsPermission = useMemo(
    () => rowActions.some((a) => canAccessRequirement(ability, a.permission, true)),
    [rowActions, ability],
  );
  const showActions = !hideActions && hasActionsPermission;

  return (
    <Loading variant="overlay" visible={reversingJournalId !== null}>
    <Table striped highlightOnHover withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <Table.Th w={50} ta="center">
            STT
          </Table.Th>
          <Table.Th>Mã phiếu thu</Table.Th>
          <Table.Th>Ngày thu</Table.Th>
          <Table.Th>Tài khoản ngân hàng</Table.Th>
          <Table.Th>Trạng thái</Table.Th>
          <Table.Th ta="right">Số tiền</Table.Th>
          <Table.Th>Ghi chú</Table.Th>
          {showActions && (
            <Table.Th w={100} ta="center">
              Thao tác
            </Table.Th>
          )}
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {journals.map((j, index) => (
          <Table.Tr key={j.id}>
            <Table.Td ta="center">{index + 1}</Table.Td>
            <Table.Td>
              <Anchor size="sm" onClick={() => navigate(DEBT_RECEIPT_DETAIL_PAGE.replace(":id", j.id))}>
                {j.code}
              </Anchor>
            </Table.Td>
            <Table.Td>{formatDate(j.paymentDate)}</Table.Td>
            <Table.Td>
              {j.bankAccountId == null
                ? formatBankAccount(null)
                : bankAccountMap[j.bankAccountId]
                  ? `${bankAccountMap[j.bankAccountId].bankName} - ${bankAccountMap[j.bankAccountId].accountHolder}`
                  : "—"}
            </Table.Td>
            <Table.Td>
              <CustomBadge
                label={RECEIPT_STATUS_LABEL[j.status] ?? j.status}
                color={RECEIPT_STATUS_COLOR[j.status] ?? "gray"}
              />
            </Table.Td>
            <Table.Td ta="right" fw={500}>
              {formatCurrency(j.amount)}
            </Table.Td>
            <Table.Td c="dimmed">{j.notes ?? "—"}</Table.Td>
            {showActions && (
              <Table.Td ta="center">
                <GroupActions row={j} actions={rowActions} />
              </Table.Td>
            )}
          </Table.Tr>
        ))}
      </Table.Tbody>

      <Table.Tfoot>
        <Table.Tr>
          <Table.Td colSpan={5} ta="right" fw={600}>
            Tổng đã thu
          </Table.Td>
          <Table.Td ta="right" fw={700} c="blue">
            {formatCurrency(journals.reduce((sum, j) => sum + j.amount, 0))}
          </Table.Td>
          <Table.Td />
          {showActions && <Table.Td />}
        </Table.Tr>
      </Table.Tfoot>
    </Table>
    </Loading>
  );
};

export default PaymentJournalsTable;
